import { z } from 'zod';

export const createProjectShareSchema = z.object({
  inviteeEmail: z.string().email(),
  expiresAt: z.string().datetime().nullable().optional(),
});
export type CreateProjectShareInput = z.infer<typeof createProjectShareSchema>;

/**
 * The optional date range a share recipient can narrow the public view to
 * (`GET /shared/:token`). Both bounds are optional and independent — no
 * range at all means "everything tracked on this project", which is what an
 * emailed link opens on.
 */
export const sharedProjectViewQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});
export type SharedProjectViewQuery = z.infer<typeof sharedProjectViewQuerySchema>;

/** The file formats a share recipient can download the selected hours as. */
export const SHARED_EXPORT_FORMATS = ['pdf', 'xlsx'] as const;
export type SharedExportFormat = (typeof SHARED_EXPORT_FORMATS)[number];

/**
 * `GET /shared/:token/export` — the same optional date range the page is
 * narrowed to, plus the file format. `tz` (IANA name) and `lang` are the
 * visitor's own: a range is cut on the visitor's local day boundaries, so the
 * dates and headings in the file have to be read in that same zone and
 * language or the report would disagree with the page it was downloaded from.
 * Both are best-effort hints — an unknown value falls back to UTC/English
 * rather than failing the download.
 */
export const sharedProjectExportQuerySchema = sharedProjectViewQuerySchema.extend({
  format: z.enum(SHARED_EXPORT_FORMATS),
  tz: z.string().max(64).optional(),
  lang: z.string().max(16).optional(),
});
export type SharedProjectExportQuery = z.infer<typeof sharedProjectExportQuerySchema>;
